import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IndicationRequest, UserRecord, userService } from "@/services";
import {
  RegisterStepsModal,
  RegisteStepsrModalData,
  TermsModal,
} from "@/components";
import { useUser } from "@/contexts";

const RegisterScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading } = useUser();

  const [searchParams] = useSearchParams();

  const referrerSlug = searchParams.get("user");
  const referralCode = searchParams.get("ref");

  const [formData, setFormData] = useState<UserRecord>({
    gender: "",
    seeking: "",
  });

  const [touched, setTouched] = useState({
    gender: false,
    seeking: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showTerms, setShowTerms] = useState(false);
  const [showRegisterSteps, setShowRegisterSteps] = useState(false);

  const errors = useMemo(() => {
    const genderValue = formData.gender;
    const seekingValue = formData.seeking;

    const genderError = genderValue ? null : "Selecione uma opção.";
    const seekingError = seekingValue ? null : "Selecione uma opção.";

    return {
      gender: genderError,
      seeking: seekingError,
    };
  }, [formData.gender, formData.seeking]);

  useEffect(() => {
    if (!loading && user) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  const _createUser = async (modalData: RegisteStepsrModalData) => {
    try {
      setIsSubmitting(true);

      const { profilePhoto, ...restModalData } = modalData;

      const payload: UserRecord = {
        ...formData,
        ...restModalData,
        referralSource: referralCode
          ? `link_${referrerSlug}`
          : formData.referralSource,
      };

      let userIndication: IndicationRequest | undefined = undefined;

      if (referrerSlug || referralCode) {
        userIndication = {
          referrerSlug: referrerSlug || "",
          referralCode: referralCode || "",
          referredUsername: restModalData.username || formData.username || "",
        } as IndicationRequest;
      }

      const result = await userService.createUser(payload, {
        profilePhoto,
        userIndication: userIndication,
      });

      if (result.success) {
        setShowRegisterSteps(false);

        navigate("/cadastro/sucesso");
      } else {
        const message = result.error.detail ?? result.error.title;

        console.error("Erro ao cadastrar: ", result.error);

        setShowRegisterSteps(false);
        navigate("/cadastro/erro", { state: { message } });
      }
    } catch (error) {
      console.error(error);
      navigate("/cadastro/erro", {
        state: { message: "Ocorreu um erro inesperado." },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const _completeRegister = (acceptedTerms: boolean) => {
    setShowTerms(false);

    if (acceptedTerms) {
      setFormData((prev) => ({ ...prev, acceptedTerms: true }));
      setShowRegisterSteps(true);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name } = e.target;

    if (name === "gender" || name === "seeking") {
      setTouched((prev) => ({ ...prev, [name]: true }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setTouched({
      gender: true,
      seeking: true,
    });

    if (errors.gender || errors.seeking) {
      return;
    }

    setShowTerms(true);
  };

  return (
    <section className="stats">
      <div className="container">
        <div className="stat-card cta-content center auth-card">
          <h1 className="auth-title">Vamos Começar?</h1>
          <p className="auth-subtitle">
            Escolha suas preferências básicas para o seu perfil.
          </p>
          <form className="auth-form" onSubmit={handleSubmit}>
            <div>
              <label>Qual o seu gênero?</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                onBlur={handleBlur}
                aria-invalid={touched.gender && Boolean(errors.gender)}
                aria-describedby={
                  touched.gender && errors.gender ? "gender-error" : undefined
                }
              >
                <option value="" disabled>
                  Selecione uma opção
                </option>
                <option value="Masculino">Masculino</option>
                <option value="Feminino">Feminino</option>
              </select>
              {touched.gender && errors.gender ? (
                <div id="gender-error" className="form-error">
                  {errors.gender}
                </div>
              ) : null}
            </div>

            <div>
              <label>Está em busca de quê?</label>
              <select
                name="seeking"
                value={formData.seeking}
                onChange={handleChange}
                onBlur={handleBlur}
                aria-invalid={touched.seeking && Boolean(errors.seeking)}
                aria-describedby={
                  touched.seeking && errors.seeking
                    ? "seeking-error"
                    : undefined
                }
              >
                <option value="" disabled>
                  Selecione uma opção
                </option>
                <option value="Homens">Homens</option>
                <option value="Mulheres">Mulheres</option>
                <option value="Homens e mulheres">Homens e mulheres</option>
              </select>
              {touched.seeking && errors.seeking ? (
                <div id="seeking-error" className="form-error">
                  {errors.seeking}
                </div>
              ) : null}
            </div>

            <div className="auth-actions">
              <button
                className="btn-primary"
                type="submit"
                disabled={
                  isSubmitting ||
                  Boolean(errors.gender) ||
                  Boolean(errors.seeking)
                }
              >
                {isSubmitting ? "Enviando..." : "Continuar"}
              </button>

              <p className="auth-disclaimer">
                Ao continuar, você <strong>declara ter 18 anos ou mais</strong>,
                concorda em receber nossos e-mails e aceita nossos{" "}
                <a href="/termos">Termos e condições de uso</a> e{" "}
                <a href="/politica">Políticas de privacidade</a>. Reforçamos que
                atividades ilícitas ou comerciais, como prostituição, são
                proibidas.{" "}
                <strong>
                  Este site não é destinado a acompanhantes ou garotas de
                  programa.
                </strong>
              </p>
            </div>
          </form>
        </div>
        <div className="login-links center">
          <span>
            Já tem uma conta no Mimo Cupido? <a href="/login">Fazer login</a>
          </span>
        </div>
      </div>

      <TermsModal
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
        onSubmit={_completeRegister}
      />

      <RegisterStepsModal
        isOpen={showRegisterSteps}
        onClose={() => setShowRegisterSteps(false)}
        onSubmit={_createUser}
        isLoading={isSubmitting}
      />
    </section>
  );
};

export default RegisterScreen;
